import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { NotificationCardError } from './NotificationCardError';
import { PlaceholderBadges } from './PlaceholderBadges';
import { PlaceholdersInfoTooltip } from './PlaceholdersInfoTooltip';
import { TemplatePreview } from './TemplatePreview';
import { useInsertPlaceholder } from './useInsertPlaceholder';

/**
 * How the supported placeholders are surfaced:
 * - `compact`: an info tooltip beside the message label plus a badge row.
 * - `described`: a labelled section under the message, listing every badge with
 *   its description.
 */
export type PlaceholdersDisplay = 'compact' | 'described';

interface NotificationMessageEditorProps {
  /** `id` of the textarea, linked to the message label. */
  id: string;
  /** Prefix for the body, placeholder, preview and error `data-testid`s. */
  testIdPrefix: string;
  body: string;
  onBodyChange: (value: string) => void;
  allowedParams: string[];
  /** Validation error for the body, rendered under the preview. */
  error?: string | null;
  rows?: number;
  /** Overrides the sample values used in the preview (e.g. `{ years }`). */
  previewParams?: Record<string, string>;
  placeholdersDisplay?: PlaceholdersDisplay;
}

/**
 * Message editing block of a notification card: the body textarea with its
 * click-to-insert placeholders, a live Slack preview and inline validation.
 * Owns the caret-aware placeholder insertion; the body itself stays owned by
 * the containing card.
 */
export const NotificationMessageEditor: React.FC<NotificationMessageEditorProps> = ({
  id,
  testIdPrefix,
  body,
  onBodyChange,
  allowedParams,
  error,
  rows = 4,
  previewParams,
  placeholdersDisplay = 'compact',
}) => {
  const { t } = useTranslation('admin');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const insertPlaceholder = useInsertPlaceholder(body, onBodyChange, textareaRef);
  const described = placeholdersDisplay === 'described';

  const textarea = (
    <Textarea
      id={id}
      ref={textareaRef}
      value={body}
      onChange={(e) => onBodyChange(e.target.value)}
      rows={rows}
      className="font-mono"
      data-testid={`${testIdPrefix}-body`}
    />
  );

  const badges = (
    <PlaceholderBadges
      allowedParams={allowedParams}
      onInsert={insertPlaceholder}
      testIdPrefix={testIdPrefix}
      withDescriptions={described}
    />
  );

  const preview = (
    <TemplatePreview
      body={body}
      previewParams={previewParams}
      testId={`${testIdPrefix}-preview`}
    />
  );

  const errorRow = error ? (
    <NotificationCardError message={error} testId={`${testIdPrefix}-error`} />
  ) : null;

  if (described) {
    return (
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor={id}>{t('notificationTemplates.messageLabel')}</Label>
          {textarea}
        </div>

        <div className="space-y-2">
          <p className="text-sm font-medium">{t('notificationTemplates.placeholdersLabel')}</p>
          <p className="text-xs text-muted-foreground">
            {t('notificationTemplates.placeholdersHint')}
          </p>
          {badges}
        </div>

        <div className="space-y-2">
          <p className="text-sm font-medium">{t('notificationTemplates.previewLabel')}</p>
          {preview}
        </div>

        {errorRow}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Label htmlFor={id}>{t('notificationTemplates.messageLabel')}</Label>
        <PlaceholdersInfoTooltip allowedParams={allowedParams} testIdPrefix={testIdPrefix} />
      </div>
      {badges}
      {textarea}
      <div className="space-y-1">
        <p className="text-xs font-medium text-muted-foreground">
          {t('notificationTemplates.previewLabel')}
        </p>
        {preview}
      </div>
      {errorRow}
    </div>
  );
};
